import Constants from '@/Constants';
import List from '@/models/List';
import { Program } from '@/models/Program';
import { ViewType } from '@/models/ViewType';
import { AnyAction, Reducer } from 'redux';
import { PURGE } from 'redux-persist';
import _ from 'underscore';
import { CourseStudentView } from '../list/types';
import { ListProgramClassStudentView, ProgramClassStudentView, ProgramState, ProgramTypes } from './types';

const initialStateProgramList: ListProgramClassStudentView = {
    list: [] as ProgramClassStudentView[],
    total: 0,
    totalToExpire: 0,
    loading: true,
};

const initialStateCurrentProgramClassStudentView = { courseStudentViews: [] as CourseStudentView[] } as ProgramClassStudentView;

const INITIAL_STATE: ProgramState = {
    data: {
        programs: [] as Program[],
    },
    programData: {} as Program,
    prerequisitesEnabled: false,
    loading: true,
    hasError: false,
    error: undefined,
    success: false,
    courseAdded: false,
    courseRemoved: false,
    courses: [],
    listData: {
        listLastPublishedStudentView: { ...initialStateProgramList },
        listInProgressStudentView: { ...initialStateProgramList },
        listSearchResultStudentView: { ...initialStateProgramList },
        listCompletedStudentView: { ...initialStateProgramList },
        listFavoriteStudentView: { ...initialStateProgramList },
        listEssentialStudentView: { ...initialStateProgramList },
        listTrainingsEnrolledStudentView: { ...initialStateProgramList },
    },
    currentProgramClassStudentView: { ...initialStateCurrentProgramClassStudentView },
};

const processProgramRequestSuccess = (state: ProgramState, action: AnyAction, listName: ViewType) => {
    const currentList: ProgramClassStudentView[] = [...state.listData[listName].list];
    const newList: ProgramClassStudentView[] = [...action.payload.data];
    const mergedlist: ProgramClassStudentView[] = currentList.concat(newList);
    const resultList: ProgramClassStudentView[] = (_.chain(mergedlist)
        .filter((item: ProgramClassStudentView) => Boolean(item.classEnrollment && item.classEnrollment.id))
        .uniq(false, (item: ProgramClassStudentView) => item.classEnrollment.id)
        .value() as unknown) as ProgramClassStudentView[];

    return {
        ...state,
        loading: false,
        hasError: false,
        listData: {
            ...state.listData,
            [listName]: {
                ...state.listData[listName],
                list: resultList,
                total: action.payload.total || 0,
                totalToExpire: action.payload.totalToExpire || 0,
                loading: false,
            },
        },
    };
};

const removeProgramFromList = (state: ProgramState, action: AnyAction, listName: ViewType, programId: number) => {
    const currentList: ProgramClassStudentView[] = [...state.listData[listName].list];
    const resultList: ProgramClassStudentView[] = currentList.filter(
        (item: ProgramClassStudentView) => item.classEnrollment.programClass.program.id !== programId,
    );
    return {
        ...state,
        loading: false,
        hasError: false,
        listData: {
            ...state.listData,
            [listName]: {
                ...state.listData[listName],
                list: resultList,
                loading: false,
            },
        },
    };
};

const reducer: Reducer<ProgramState> = (state: ProgramState = INITIAL_STATE, action) => {
    switch (action.type) {
        case PURGE:
            return { ...INITIAL_STATE };
        case ProgramTypes.CREATE_PROGRAM_REQUEST:
            return { ...state, loading: true, hasError: false };
        case ProgramTypes.CREATE_PROGRAM_SUCCESS:
            return { ...state, success: true, loading: false, hasError: false, programData: action.payload };
        case ProgramTypes.CREATE_PROGRAM_FAILURE:
            return { ...state, loading: false, hasError: true, error: action.payload };
        case ProgramTypes.UPDATE_PROGRAM_REQUEST:
            return { ...state, loading: true, hasError: false };
        case ProgramTypes.UPDATE_PROGRAM_SUCCESS:
            return { ...state, success: true, loading: false, hasError: false };
        case ProgramTypes.UPDATE_PROGRAM_FAILURE:
            return { ...state, loading: false, hasError: true, error: action.payload };
        case ProgramTypes.GET_PROGRAM_REQUEST:
            return { ...state, loading: true, success: false, hasError: false };
        case ProgramTypes.GET_PROGRAM_SUCCESS:
            return { ...state, loading: false, hasError: false, programData: action.payload };
        case ProgramTypes.GET_PROGRAM_CLEAR:
            return { ...state, loading: false, hasError: false, programData: {} };
        case ProgramTypes.GET_PROGRAMS_REQUEST:
            return { ...state, loading: true, success: false, hasError: false };
        case ProgramTypes.GET_PROGRAMS_SUCCESS:
            return { ...state, loading: false, hasError: false, data: { ...state.data, programs: action.payload } };
        case ProgramTypes.REORDER_PROGRAM_COURSE_REQUEST:
            return { ...state, loading: true, success: false, hasError: false };
        case ProgramTypes.REORDER_PROGRAM_COURSE_SUCCESS:
            return { ...state, loading: false, hasError: false };
        case ProgramTypes.GET_PROGRAM_COURSES_LIST_REQUEST:
            return { ...state, loading: true, success: false, hasError: false };
        case ProgramTypes.GET_PROGRAM_COURSES_LIST_SUCCESS:
            return { ...state, loading: false, hasError: false, courses: action.payload };
        case ProgramTypes.GET_PROGRAM_PREREQUISITES_SUCCESS:
            return { ...state, prerequisitesEnabled: action.payload };
        case ProgramTypes.REMOVE_PROGRAM_COURSES_REQUEST:
            const indexToRemove = state.courses.findIndex((item) => item.id === action.payload.courseId);
            return {
                ...state,
                loading: true,
                success: false,
                hasError: false,
                courseRemoved: false,
                lastRemovedCourseId: action.payload.courseId,
                courses: [...state.courses.slice(0, indexToRemove), ...state.courses.slice(indexToRemove + 1)],
            };
        case ProgramTypes.REMOVE_PROGRAM_COURSES_SUCCESS:
            return { ...state, loading: false, hasError: false, courseRemoved: true };
        case ProgramTypes.REMOVE_PROGRAM_COURSES_FAILURE:
            return { ...state, loading: false, success: false, hasError: true, courseRemoved: false, lastRemovedCourseId: undefined };
        case ProgramTypes.ADD_COURSE_IN_PROGRAM_REQUEST:
            return { ...state, loading: true, courseAdded: false, courses: [...state.courses, action.payload.course] };
        case ProgramTypes.ADD_COURSE_IN_PROGRAM_SUCCESS:
            return { ...state, loading: false, hasError: false, courseAdded: true };
        case ProgramTypes.ADD_COURSE_IN_PROGRAM_FAILURE:
            const courses: List[] = [...state.courses];
            courses.pop();
            return { ...state, hasError: true, courseAdded: false, courses };
        case ProgramTypes.CREATE_PROGRAM_COURSE_REQUEST:
            return { ...state, loading: true, hasError: false };
        case ProgramTypes.CREATE_PROGRAM_COURSE_SUCCESS:
            return { ...state, success: true, loading: false, hasError: false };
        case ProgramTypes.CREATE_PROGRAM_COURSE_FAILURE:
            return { ...state, loading: false, success: false, hasError: true, error: action.payload };
        case ProgramTypes.CREATE_PROGRAM_COURSE_CLEAR:
            return { ...INITIAL_STATE };
        case ProgramTypes.PUBLISH_PROGRAM_REQUEST:
            return { ...state, loading: true };
        case ProgramTypes.CATEGORIZED_SUCCESS:
            const listName: ViewType = (Constants.listCategorizedPrefix + action.payload.categoryId) as ViewType;
            return processProgramRequestSuccess(state, action, listName);
        case ProgramTypes.ADD_PROGRAM_BY_CATEGORY:
            return {
                ...state,
                listData: {
                    ...state.listData,
                    [Constants.listCategorizedPrefix + action.payload.categoryId]: { ...initialStateProgramList },
                },
            };
        case ProgramTypes.PUBLISH_PROGRAM_SUCCESS:
            return {
                ...state,
                programData: { ...state.programData, status: 'PUBLISHED' },
                loading: false,
                success: true,
            };
        case ProgramTypes.PUBLISH_PROGRAM_FAILURE:
            return {
                ...state,
                loading: false,
            };
        case ProgramTypes.GET_PROGRAM_LIST_STUDENT_VIEW_REQUEST:
            return { ...state, loading: true, hasError: false };
        case ProgramTypes.LAST_PUBLISHED_SUCCESS:
            return processProgramRequestSuccess(state, action, 'listLastPublishedStudentView');
        case ProgramTypes.LAST_PUBLISHED_CLEAR:
            return { ...state, listData: { ...state.listData, listLastPublishedStudentView: { ...initialStateProgramList } } };
        case ProgramTypes.IN_PROGRESS_SUCCESS:
            return processProgramRequestSuccess(state, action, 'listInProgressStudentView');
        case ProgramTypes.ESSENTIALS_SUCCESS:
            return processProgramRequestSuccess(state, action, 'listEssentialStudentView');
        case ProgramTypes.IN_PROGRESS_CLEAR:
            return { ...state, listData: { ...state.listData, listInProgressStudentView: { ...initialStateProgramList } } };
        case ProgramTypes.GET_PROGRAM_LIST_STUDENT_VIEW_FAILURE:
            return { ...state, loading: false, hasError: true, error: action.payload };
        case ProgramTypes.GET_PROGRAM_CLASS_STUDENT_VIEW_REQUEST:
            return { ...state, loading: true, hasError: false };
        case ProgramTypes.GET_PROGRAM_CLASS_STUDENT_VIEW_SUCESS:
            return {
                ...state,
                loading: false,
                hasError: false,
                programData: action.payload.classEnrollment.programClass.program,
                currentProgramClassStudentView: action.payload,
            };
        case ProgramTypes.GET_PROGRAM_CLASS_STUDENT_VIEW_FAILURE:
            return { ...state, loading: false, hasError: true, error: action.payload };
        case ProgramTypes.REORDER_PROGRAM_COURSE_FAILURE:
        case ProgramTypes.GET_PROGRAM_PREREQUISITES_FAILURE:
        case ProgramTypes.GET_PROGRAM_COURSES_LIST_FAILURE:
        case ProgramTypes.GET_PROGRAM_FAILURE:
        case ProgramTypes.GET_PROGRAMS_FAILURE:
            return { ...state, loading: false, hasError: true, success: false };
        case ProgramTypes.CLEAR_PROGRAM_CLASS_STUDENT_VIEW: {
            return { ...state, currentProgramClassStudentView: { ...initialStateCurrentProgramClassStudentView } };
        }
        case ProgramTypes.CLEAR_PROGRAM: {
            return { ...INITIAL_STATE };
        }
        case ProgramTypes.SET_PROGRAM_READY_SUCCESS: {
            return { ...state, programData: { ...state.programData, type: 'READY' } };
        }
        case ProgramTypes.SET_PROGRAM_READY_FAILURE: {
            return { ...state, programData: { ...state.programData, type: 'BUILDING' } };
        }
        case ProgramTypes.SEARCHED_REQUEST:
            return { ...state, hasError: false };
        case ProgramTypes.SEARCHED_SUCCESS:
            return processProgramRequestSuccess(state, action, 'listSearchResultStudentView');
        case ProgramTypes.SEARCHED_FAILURE:
            return { ...state, hasError: true, error: action.payload };
        case ProgramTypes.SEARCHED_CLEAR:
            return {
                ...state,
                listData: {
                    ...state.listData,
                    listSearchResultStudentView: { ...initialStateProgramList },
                },
            };
        case ProgramTypes.UPDATE_PROGRAM_CLASS_STUDENT_VIEW_REQUEST:
            return { ...state, hasError: false };
        case ProgramTypes.UPDATE_PROGRAM_CLASS_STUDENT_VIEW_SUCCESS:
            return {
                ...state,
                hasError: false,
                programData: { ...state.programData, ...action.payload.classEnrollment.programClass.program },
                currentProgramClassStudentView: { ...state.currentProgramClassStudentView, ...action.payload },
            };
        case ProgramTypes.UPDATE_PROGRAM_CLASS_STUDENT_VIEW_FAILURE:
            return { ...state, hasError: true, error: action.payload };
        case ProgramTypes.COMPLETED_SUCCESS:
            return processProgramRequestSuccess(state, action, 'listCompletedStudentView');
        case ProgramTypes.FAVORITE_SUCCESS:
            return processProgramRequestSuccess(state, action, 'listFavoriteStudentView');
        case ProgramTypes.COMPLETED_CLEAR: {
            return {
                ...state,
                listData: {
                    ...state.listData,
                    listCompletedStudentView: { ...initialStateProgramList },
                },
            };
        }
        case ProgramTypes.FAVORITES_CLEAR:
            return { ...state, listData: { ...state.listData, listFavoriteStudentView: { ...initialStateProgramList } } };
        case ProgramTypes.REMOVE_PROGRAM_FROM_LIST:
            return removeProgramFromList(state, action, action.payload.viewType, action.payload.programId);
        case ProgramTypes.TRAININGS_SUCCESS:
            return processProgramRequestSuccess(state, action, 'listTrainingsEnrolledStudentView');
        case ProgramTypes.TRAININGS_CLEAR: {
            return {
                ...state,
                listData: {
                    ...state.listData,
                    listTrainingsEnrolledStudentView: { ...initialStateProgramList },
                },
            };
        }
        default:
            return state;
    }
};

export default reducer;
